import React from 'react';
import { cn } from '../../../shared/utils';

/**
 * SimpleReportLayout - Simplified 3-section report layout
 *
 * **Optimization:**
 * Merged BgContentWrap + ReportPageLayout into single component.
 * Reduces DOM nesting from 11 layers to 7 layers (36% reduction).
 *
 * **Architecture (CSS Grid):**
 * Uses CSS Grid with `grid-template-rows: auto 1fr auto` for optimal 3-section layout:
 * - Row 1 (auto): Controls bar - takes natural content height
 * - Row 2 (1fr): Table section - expands to fill ALL remaining space
 * - Row 3 (auto): Pagination bar - takes natural content height
 *
 * **Benefits:**
 * - No hard-coded pixel values (fully responsive)
 * - Works at any screen resolution or zoom level
 * - Footer always flush at bottom (no gaps)
 * - Semantic layout (grid designed for this pattern)
 * - Better browser support for height calculations than flexbox percentages
 *
 * **Usage:**
 * ```tsx
 * <SimpleReportLayout
 *   controls={<ReportControls ... />}
 *   pagination={<SpringPagination ... />}
 *   loading={isLoading}
 * >
 *   <DataTable ... />
 * </SimpleReportLayout>
 * ```
 */

interface SimpleReportLayoutProps {
  /** Table content (usually DataTable component) */
  children: React.ReactNode;

  /**
   * Titlul paginii de raport, randat ca `<h1>` deasupra barei de control.
   * Fara el, pagina n-are niciun titlu semantic — nici pe desktop, nici pe
   * mobil (unde `ReportMobileSummary` are deja `<h2>`-uri sub el).
   */
  title?: string;

  /** Control bar content (usually ReportControls component) */
  controls?: React.ReactNode;

  /** Pagination content (usually SpringPagination component) */
  pagination?: React.ReactNode;

  /** Loading state - shows overlay when true */
  loading?: boolean;

  /** Additional CSS classes */
  className?: string;
}

export function SimpleReportLayout({
  children,
  title,
  controls,
  pagination,
  loading,
  className
}: SimpleReportLayoutProps) {
  return (
    <div
      className={cn(
        // Suprafata de date e alba: verdele ramane al paginii, al accentelor si al
        // datelor, nu al fundalului de sub ele. Reteta traieste intr-un singur loc,
        // in main.css — aici era una dintre cele patru copii ale ei.
        // → docs/design-system/REPORT_POLISH_PLAN.md, constatarea #7
        "ecotic-report-surface",
        "grid w-full box-border p-4",
        // 🔴 Coloana de grid trebuie sa poata SCADEA sub continut. Fara asta,
        // `grid-template-columns` implicit e `auto`, iar `auto` se dimensioneaza la
        // max-content: un tabel de 1000px face coloana de 1000px intr-un parinte de
        // 360, si latimea urca pana la <html>. Masurat pe `/rapoarte/top-elevi`,
        // build de productie la 360px: document 1017px, derulare orizontala pe toata
        // pagina.
        //
        // `min-width: 0` pe copii NU rezolva — masurat, tot 1017: pe o coloana `auto`,
        // dimensionarea nu se uita la min-width-ul itemului. Trebuie schimbata
        // coloana insasi.
        "[grid-template-columns:minmax(0,1fr)]",
        // Sub `md`, hamburgerul plutitor sta la y=20..64 si NU e in flux (vezi
        // `ecotic-hamburger-floating`). Fara spatiul asta, primul control al
        // raportului ajunge sub el: masurat pe DEV la 360px, un tap in coltul din
        // stanga-sus al selectorului de editie deschidea meniul lateral, nu lista
        // de editii.
        //
        // 🔴 Valoarea se calibreaza pe PRIMUL PIXEL al blocului de control, nu pe
        // control. Prima varianta (`pt-14`) a fost masurata pe butonul
        // selectorului (ajunsese la y=80, curat) — dar `EditionSelector` are o
        // ETICHETA deasupra lui, iar aia ramasese la y=57, adica 7 px sub
        // hamburger: pe ecran se citea „ED…IE". Sonda de hit-test n-avea cum s-o
        // vada, fiindca o eticheta nu e control. S-a vazut pe captura.
        "pt-18 md:pt-4",
        // De la `md` in sus: inaltime fixa, cu bara de sus si cea de jos ancorate,
        // iar tabelul deruleaza in interior. Sub `md` nu exista tabel (vezi
        // `ReportPageLayout`), deci nu exista nici motiv de inaltime fixa: randurile
        // sunt `auto`, containerul creste cat are nevoie si deruleaza PAGINA.
        // Un scroller imbricat pe telefon inseamna ca degetul deruleaza altceva
        // decat crede utilizatorul — constatarea de la 360px, sesiunea S3.
        "md:h-full md:[grid-template-rows:auto_1fr_auto]",
        className
      )}
    >
      {/* SECTION 1: Fixed Controls Bar
          Titlul intra IN aceeasi celula de grid ca si controalele (randul 1,
          "auto"), nu inaintea grid-ului: `pt-18 md:pt-4` de mai sus e calibrat
          pe primul pixel al acestei celule, ca sa treaca de hamburgerul
          plutitor de pe mobil (vezi comentariul de la clasa). Un h1 pus
          DEASUPRA grid-ului ar iesi de sub acel padding si s-ar suprapune cu
          hamburgerul. */}
      {(title || controls) && (
        <div>
          {title && (
            <h1 className="text-fluid-xl font-bold text-foreground mb-3">{title}</h1>
          )}
          {controls}
        </div>
      )}

      {/* SECTION 2: Scrollable Table Container - extends to bottom while keeping pagination visible */}
      <div className="relative md:overflow-hidden">
        {/* `overflow-x-auto` FARA prefix: sub `md` nu exista niciun proprietar al
            depasirii pe orizontala. Containerul propriu al lui shadcn e neutralizat
            deliberat (`[data-slot=table-container] { overflow-x: visible }` in
            main.css), iar cel de aici era `md:`, deci sub 768px nimeni nu taia si
            tabelul impingea pagina.
            Nu contrazice regula de mai sus despre scrollere imbricate pe telefon:
            aia e despre axa VERTICALA, unde un scroller intern fura gestul de
            derulare a paginii. Pe orizontala pagina n-are ce pierde — ori deruleaza
            tabelul in cadrul lui, ori se rupe layout-ul. `md:overflow-y-auto` ramane
            prefixat tocmai de aceea. */}
        <div className="ecotic-table-scrollbar overflow-x-auto md:h-full md:overflow-y-auto md:pr-4">
          {children}
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-30">
            <div className="text-center">
              <div className="text-lg font-medium text-gray-700">
                Se încarcă...
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SECTION 3: Fixed Pagination Bar
          Fara muchie proprie: SpringPagination isi poarta singur `ecotic-report-rail`.
          Inainte erau doua linii de 1px suprapuse — una gri de aici, una verde de
          acolo (border-t necolorat). */}
      {pagination && (
        <div className="bg-white/30">
          {pagination}
        </div>
      )}
    </div>
  );
}

export default SimpleReportLayout;
